// src/components/blog/components/useTableOfContentsScrollSpy.ts

'use client'

import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import type { TOCItem } from '~/lib/blog/types/blog.types'

/** Tuning for the {@link useTableOfContentsScrollSpy} IntersectionObserver. */
export interface ScrollSpyOptions {
  /** IntersectionObserver `rootMargin` (default centres detection in the viewport). */
  rootMargin?: string
  /** IntersectionObserver `threshold` (default 0.2). */
  threshold?: number
}

/**
 * Tracks which heading is currently in view and returns its id.
 *
 * Single source of truth for the glossary/blog table-of-contents scroll-spy —
 * the same `IntersectionObserver` block used to be copy-pasted in
 * `AnimatedTableOfContents` and the (now-deleted) `TableOfContentsClient`. Owns
 * ONLY "which heading is active"; TOC-container concerns (auto-scrolling the
 * active row into view, scroll-indicator visibility) stay in the component.
 *
 * Client-only (reads `document`); safe to call with an empty `items` array.
 *
 * Returns a `[activeId, setActiveId]` tuple: the observer updates `activeId` as
 * headings scroll into view, and the setter lets the caller set it imperatively
 * (e.g. on a TOC click that scrolls an earlier heading *above* the active band,
 * where the observer would otherwise never fire and the old row would stay lit).
 *
 * @param items - The TOC items whose heading elements are observed.
 * @param options - Optional {@link ScrollSpyOptions} overrides.
 * @returns `[activeId, setActiveId]` — active heading id (empty until one intersects) + its setter.
 */
export function useTableOfContentsScrollSpy(
  items: TOCItem[],
  options: ScrollSpyOptions = {},
): [string, Dispatch<SetStateAction<string>>] {
  const { rootMargin = '-25% 0px -35% 0px', threshold = 0.2 } = options
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    if (items.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.filter((entry) => entry.isIntersecting)
        const intersectingId = intersecting[0]?.target.id
        if (intersectingId) {
          setActiveId(intersectingId)
        }
      },
      { rootMargin, threshold },
    )

    items.forEach((item) => {
      const element = document.getElementById(item.id)
      if (element) observer.observe(element)
    })

    return () => observer.disconnect()
  }, [items, rootMargin, threshold])

  return [activeId, setActiveId]
}
